import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_DARKER, BLUE_LIGHTEST} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingDuotoneIcon from 'ui-library/icons/BenchlingDuotoneIcon';
import {assertNever} from 'util/Types';

export enum StudyDesignTabEditState {
  EDITING = 'EDITING',
  SAVING = 'SAVING',
  SAVED = 'SAVED',
}

interface StudyDesignTabSaveButtonProps {
  isSaveDisabled: boolean;
  saveDisabledTooltip?: string;
  onSave: () => void;
}

export function StudyDesignTabSaveButton(props: StudyDesignTabSaveButtonProps): JSX.Element {
  return (
    <Tooltip
      isDisabled={!props.isSaveDisabled || props.saveDisabledTooltip == null}
      text={props.saveDisabledTooltip}
    >
      <Button
        buttonStyle={ButtonStyle.PRIMARY}
        className={css(styles.button)}
        size={ButtonSize.MINI}
        text="Save"
        onClick={props.onSave}
        disabled={props.isSaveDisabled}
        dataTestAttrs={{
          component: 'StudyDesignTabSavingButtons',
          element: 'saveButton',
        }}
      />
    </Tooltip>
  );
}

export function StudyDesignTabSavingIndication(): JSX.Element {
  return (
    <Button
      buttonStyle={ButtonStyle.PRIMARY}
      className={css(styles.button)}
      size={ButtonSize.MINI}
      text="Saving"
      disabled={true}
      icon={
        <div className={css(styles.spinnerIcon)}>
          <BenchlingDuotoneIcon
            icon="spinner-third"
            primaryColor={BLUE_DARKER}
            secondaryColor={BLUE_LIGHTEST}
            spin={true}
          />
        </div>
      }
    />
  );
}

export interface StudyDesignTabSavingButtonsProps extends StudyDesignTabSaveButtonProps {
  editState: StudyDesignTabEditState;
  onCancel: () => void;
  onEdit: () => void;
}

export default function StudyDesignTabSavingButtons(props: StudyDesignTabSavingButtonsProps): JSX.Element {
  switch (props.editState) {
    case StudyDesignTabEditState.EDITING:
      return (
        <div>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            className={css(styles.button)}
            size={ButtonSize.MINI}
            text="Cancel"
            onClick={props.onCancel}
            disabled={false}
          />
          <StudyDesignTabSaveButton
            isSaveDisabled={props.isSaveDisabled}
            saveDisabledTooltip={props.saveDisabledTooltip}
            onSave={props.onSave}
          />
        </div>
      );
    case StudyDesignTabEditState.SAVING:
      return <StudyDesignTabSavingIndication />;
    case StudyDesignTabEditState.SAVED:
      return (
        <Button
          icon="edit"
          buttonStyle={ButtonStyle.PRIMARY}
          className={css(styles.button)}
          size={ButtonSize.MINI}
          text="Edit"
          onClick={props.onEdit}
          disabled={false}
          dataTestAttrs={{
            component: 'StudyDesignTabSavingButtons',
            element: 'editButton',
          }}
        />
      );
    default:
      return assertNever(props.editState);
  }
}

export const styles = StyleSheet.create({
  button: {
    marginLeft: '6px',
  },
  spinnerIcon: {
    opacity: 0.5,
  },
});
